import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { coachSafeAreas, coachViewport, coachViewportArea } from './coachViewport'

type Position = {
  x: number
  y: number
}

type DragState = {
  pointerId: number
  pointerX: number
  pointerY: number
  panelX: number
  panelY: number
  handle: HTMLButtonElement
}

const KEYBOARD_STEP = 24

function clampPosition(position: Position, panel: HTMLElement): Position {
  const bounds = panel.getBoundingClientRect()
  const area = coachViewportArea(panel)
  const maxX = Math.max(area.left, area.right - bounds.width)
  const maxY = Math.max(area.top, area.bottom - bounds.height)

  return {
    x: Math.min(maxX, Math.max(area.left, position.x)),
    y: Math.min(maxY, Math.max(area.top, position.y)),
  }
}

function samePosition(left: Position | null, right: Position) {
  return left?.x === right.x && left.y === right.y
}

export function useFloatingCoachWindow(isOpen: boolean) {
  const panelRef = useRef<HTMLElement>(null)
  const headerRef = useRef<HTMLElement>(null)
  const positionRef = useRef<Position | null>(null)
  const dragRef = useRef<DragState | null>(null)
  const [position, setPosition] = useState<Position | null>(null)
  const [isDragging, setDragging] = useState(false)
  const [canDrag, setCanDrag] = useState(true)

  const updatePosition = useCallback((nextPosition: Position) => {
    const panel = panelRef.current
    if (!panel) return

    const clampedPosition = clampPosition(nextPosition, panel)
    setPosition(current => (samePosition(current, clampedPosition) ? current : clampedPosition))
  }, [])

  const stopDragging = useCallback(() => {
    const drag = dragRef.current
    dragRef.current = null
    if (drag?.handle.hasPointerCapture?.(drag.pointerId)) {
      drag.handle.releasePointerCapture(drag.pointerId)
    }
    setDragging(false)
  }, [])

  useLayoutEffect(() => {
    if (!isOpen) return
    const panel = panelRef.current
    if (!panel) return

    const keepInsideViewport = () => {
      const area = coachViewportArea(panel)
      panel.style.setProperty('--coach-available-width', area.width + 'px')
      panel.style.setProperty('--coach-available-height', area.height + 'px')
      panel.dataset.tight = String(area.height < 120)
      panel.dataset.compact = String(area.height < 360)
      const headerHeight = headerRef.current?.offsetHeight ?? 0
      const draggable = area.height >= headerHeight + 8
      setCanDrag(draggable)
      if (!draggable) stopDragging()

      const rect = panel.getBoundingClientRect()
      const viewport = coachViewport()
      const safe = coachSafeAreas(panel)
      const css = getComputedStyle(panel)
      const rightGap = Number.parseFloat(css.getPropertyValue('--coach-window-right')) || 18
      const bottomGap = Number.parseFloat(css.getPropertyValue('--coach-window-bottom')) || 91
      updatePosition(
        positionRef.current ?? {
          x: viewport.left + viewport.width - safe.right - rightGap - rect.width,
          y: viewport.top + viewport.height - safe.bottom - bottomGap - rect.height,
        },
      )
    }
    const viewportChanged = () => {
      stopDragging()
      keepInsideViewport()
    }
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(keepInsideViewport) : undefined

    resizeObserver?.observe(panel)
    const viewport = window.visualViewport
    window.addEventListener('resize', viewportChanged)
    window.addEventListener('orientationchange', viewportChanged)
    viewport?.addEventListener('resize', viewportChanged)
    viewport?.addEventListener('scroll', viewportChanged)
    keepInsideViewport()

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener('resize', viewportChanged)
      window.removeEventListener('orientationchange', viewportChanged)
      viewport?.removeEventListener('resize', viewportChanged)
      viewport?.removeEventListener('scroll', viewportChanged)
    }
  }, [isOpen, stopDragging, updatePosition])

  useEffect(() => {
    if (!isOpen) return

    const move = (event: PointerEvent) => {
      const drag = dragRef.current
      if (!drag || event.pointerId !== drag.pointerId) return

      const nextPosition = {
        x: drag.panelX + event.clientX - drag.pointerX,
        y: drag.panelY + event.clientY - drag.pointerY,
      }
      positionRef.current = nextPosition
      updatePosition(nextPosition)
    }
    const stop = (event: PointerEvent) => {
      if (dragRef.current?.pointerId !== event.pointerId) return
      stopDragging()
    }

    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', stop)
    window.addEventListener('pointercancel', stop)

    return () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', stop)
      window.removeEventListener('pointercancel', stop)
      stopDragging()
    }
  }, [isOpen, stopDragging, updatePosition])

  const onPointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (!canDrag || event.button !== 0 || event.isPrimary === false || dragRef.current) return
    const bounds = panelRef.current?.getBoundingClientRect()
    if (!bounds) return

    event.preventDefault()
    event.currentTarget.focus({ preventScroll: true })
    event.currentTarget.setPointerCapture?.(event.pointerId)
    dragRef.current = {
      pointerId: event.pointerId,
      pointerX: event.clientX,
      pointerY: event.clientY,
      panelX: bounds.left,
      panelY: bounds.top,
      handle: event.currentTarget,
    }
    positionRef.current = { x: bounds.left, y: bounds.top }
    setPosition(positionRef.current)
    setDragging(true)
  }

  const onKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    const direction = {
      ArrowDown: { x: 0, y: KEYBOARD_STEP },
      ArrowLeft: { x: -KEYBOARD_STEP, y: 0 },
      ArrowRight: { x: KEYBOARD_STEP, y: 0 },
      ArrowUp: { x: 0, y: -KEYBOARD_STEP },
    }[event.key]
    const bounds = panelRef.current?.getBoundingClientRect()
    if (!canDrag || !direction || !bounds) return

    event.preventDefault()
    positionRef.current = { x: bounds.left + direction.x, y: bounds.top + direction.y }
    updatePosition(positionRef.current)
  }

  const panelStyle: CSSProperties | undefined = position
    ? { left: position.x, top: position.y, right: 'auto', bottom: 'auto' }
    : undefined

  return {
    dragHandleProps: {
      onKeyDown,
      onPointerDown,
      onLostPointerCapture: stopDragging,
      disabled: !canDrag,
    },
    isDragging,
    panelRef,
    headerRef,
    panelStyle,
  }
}
